<template>
    <div class="template-field-editor">
        <div class="placeholder-legend">
            <span class="legend-label">
                <span class="material-symbols-outlined legend-icon">data_object</span>
                Click to insert:
            </span>
            <button
                v-for="placeholder in visiblePlaceholders"
                :key="placeholder.token"
                type="button"
                class="placeholder-chip"
                :class="{ 'chip-required': placeholder.required, 'chip-legacy': placeholder.legacy }"
                :title="placeholder.description + (placeholder.required ? ' (required)' : '')"
                @click="insertToken(placeholder.token)"
            >
                {{ chipLabel(placeholder.token) }}<span v-if="placeholder.required" class="required-star">*</span>
            </button>
            <button
                v-if="legacyPlaceholders.length"
                type="button"
                class="chip-toggle-legacy"
                @click="showLegacy = !showLegacy"
            >
                {{ showLegacy ? 'Hide legacy' : `Show ${legacyPlaceholders.length} legacy` }}
            </button>
        </div>

        <div class="editor-grid">
            <div class="editor-pane">
                <div class="pane-header">
                    <span class="material-symbols-outlined pane-header-icon">edit_note</span>
                    Template
                </div>
                <textarea
                    ref="editor"
                    class="template-textarea"
                    :class="{ 'has-error': validationErrors.length }"
                    :rows="channel === 'email' ? 14 : 5"
                    :value="value"
                    :placeholder="channel === 'email' ? 'Write the email HTML template…' : 'Write the SMS message…'"
                    spellcheck="false"
                    @input="$emit('input', $event.target.value)"
                ></textarea>
                <div v-if="channel === 'sms'" class="sms-meta" :class="{ 'sms-meta-multi': smsSegments > 1 }">
                    {{ previewText.length }} characters · {{ smsSegments }} SMS segment{{ smsSegments === 1 ? '' : 's' }}
                </div>
                <div v-for="(error, index) in validationErrors" :key="'err-' + index" class="validation-message error">
                    <span class="material-symbols-outlined validation-icon">error</span>
                    <span>{{ error }}</span>
                </div>
                <div
                    v-for="(warning, index) in validationWarnings"
                    :key="'warn-' + index"
                    class="validation-message warning"
                >
                    <span class="material-symbols-outlined validation-icon">warning</span>
                    <span>{{ warning }}</span>
                </div>
            </div>

            <div class="preview-pane">
                <div class="pane-header">
                    <span class="material-symbols-outlined pane-header-icon">visibility</span>
                    Preview
                    <span class="pane-header-note">sample data</span>
                </div>
                <iframe
                    v-if="channel === 'email'"
                    class="email-preview"
                    sandbox=""
                    :srcdoc="previewText"
                    title="Email template preview"
                ></iframe>
                <div v-else class="sms-preview">
                    <div class="sms-phone-header">
                        <span class="material-symbols-outlined sms-phone-icon">smartphone</span>
                        Text message
                    </div>
                    <div class="sms-bubble">{{ previewText || 'Your message preview will appear here' }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { TOKEN_REGEX, extractTokens } from './notificationTemplates.constants';

    export default {
        name: 'TemplateFieldEditor',
        props: {
            value: {
                type: String,
                default: '',
            },
            channel: {
                type: String,
                required: true,
                validator: (val) => ['email', 'sms'].includes(val),
            },
            placeholders: {
                type: Array,
                default: () => [],
            },
            sampleValues: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                showLegacy: false,
            };
        },
        computed: {
            legacyPlaceholders() {
                return this.placeholders.filter((placeholder) => placeholder.legacy);
            },
            visiblePlaceholders() {
                return this.showLegacy
                    ? this.placeholders
                    : this.placeholders.filter((placeholder) => !placeholder.legacy);
            },
            usedTokens() {
                return extractTokens(this.value);
            },
            validationErrors() {
                return this.placeholders
                    .filter((placeholder) => placeholder.required && !this.usedTokens.has(placeholder.token))
                    .map(
                        (placeholder) =>
                            `Missing required placeholder {{${placeholder.token}}} — the message would be sent without it.`
                    );
            },
            validationWarnings() {
                const allowed = new Set(this.placeholders.map((placeholder) => placeholder.token));
                return [...this.usedTokens]
                    .filter((token) => !allowed.has(token))
                    .map(
                        (token) =>
                            `Unknown placeholder {{${token}}} — it will not be replaced and will appear as literal text.`
                    );
            },
            previewText() {
                // Stored SMS templates contain literal "\n" sequences; render them as real line breaks
                let text = (this.value || '').replace(/\\n/g, '\n');
                text = text.replace(TOKEN_REGEX, (fullMatch, token) => {
                    return Object.prototype.hasOwnProperty.call(this.sampleValues, token)
                        ? this.sampleValues[token]
                        : fullMatch;
                });
                return text;
            },
            smsSegments() {
                return Math.max(1, Math.ceil(this.previewText.length / 160));
            },
        },
        methods: {
            chipLabel(token) {
                return `{{${token}}}`;
            },
            insertToken(token) {
                const editor = this.$refs.editor;
                const insertion = `{{${token}}}`;
                const start = editor.selectionStart ?? (this.value || '').length;
                const end = editor.selectionEnd ?? start;
                const updated = (this.value || '').slice(0, start) + insertion + (this.value || '').slice(end);

                this.$emit('input', updated);
                this.$nextTick(() => {
                    editor.focus();
                    editor.selectionStart = editor.selectionEnd = start + insertion.length;
                });
            },
        },
    };
</script>

<style scoped>
    .placeholder-legend {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-bottom: 12px;
    }

    .legend-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.78rem;
        font-weight: 600;
        color: #5b6b7b;
        margin-right: 2px;
    }

    .legend-icon {
        font-size: 16px;
        color: #9aa9b8;
    }

    .placeholder-chip {
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 0.75rem;
        padding: 3px 10px;
        border-radius: 5px;
        border: 1px solid rgba(var(--base-theme-color-rgb), 0.35);
        background: rgba(var(--base-theme-color-rgb), 0.08);
        color: var(--base-theme-color);
        cursor: pointer;
        line-height: 1.4;
        /* Global styles.css sets `button + button { margin-left: 15px }` — neutralize it */
        margin: 0;
        transition: background 0.12s ease, box-shadow 0.12s ease;
    }

    .placeholder-chip:hover {
        background: rgba(var(--base-theme-color-rgb), 0.16);
        box-shadow: 0 1px 3px rgba(var(--base-theme-color-rgb), 0.25);
    }

    .chip-required {
        border-color: #e3b04b;
        background: #fdf6e7;
        color: #8a6116;
    }

    .chip-required:hover {
        background: #faedcf;
        box-shadow: 0 1px 3px rgba(227, 176, 75, 0.35);
    }

    .chip-legacy {
        opacity: 0.65;
        border-style: dashed;
    }

    .chip-toggle-legacy {
        background: transparent;
        border: none;
        color: #7a8a9a;
        text-decoration: underline;
        font-size: 0.72rem;
        cursor: pointer;
        padding: 2px 4px;
        margin: 0;
    }

    .chip-toggle-legacy:hover {
        color: #5b6b7b;
    }

    .required-star {
        color: #c0392b;
        margin-left: 1px;
    }

    .editor-grid {
        display: grid;
        /* minmax(0, 1fr) keeps the textarea from blowing the column out past its half */
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 20px;
    }

    @media (max-width: 991px) {
        .editor-grid {
            grid-template-columns: minmax(0, 1fr);
        }
    }

    .editor-pane,
    .preview-pane {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .pane-header {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #7a8a9a;
        margin-bottom: 8px;
    }

    .pane-header-icon {
        font-size: 16px;
    }

    .pane-header-note {
        font-weight: 500;
        text-transform: none;
        letter-spacing: normal;
        color: #9aa9b8;
    }

    .template-textarea {
        /* Grow to match the preview pane so both panes bottom-align */
        flex: 1;
        width: 100%;
        box-sizing: border-box;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 0.8rem;
        line-height: 1.5;
        border: 1px solid #dbe3ec;
        border-radius: 6px;
        padding: 12px 14px;
        resize: vertical;
        background: #fbfcfe;
        /* Global styles.css puts a gray inset ring + pre-line whitespace on bare <textarea> */
        box-shadow: none;
        white-space: pre-wrap;
        transition: border-color 0.12s ease, box-shadow 0.12s ease;
    }

    .template-textarea:focus {
        outline: none;
        border-color: var(--base-theme-color);
        box-shadow: 0 0 0 3px rgba(var(--base-theme-color-rgb), 0.12);
        background: #ffffff;
    }

    .template-textarea.has-error {
        border-color: #c0392b;
    }

    .template-textarea.has-error:focus {
        box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
    }

    .sms-meta {
        font-size: 0.72rem;
        color: #7a8a9a;
        margin-top: 4px;
        text-align: right;
    }

    .sms-meta-multi {
        color: #b26a00;
        font-weight: 600;
    }

    .validation-message {
        display: flex;
        align-items: flex-start;
        gap: 7px;
        font-size: 0.78rem;
        margin-top: 8px;
        padding: 8px 12px;
        border-radius: 5px;
        line-height: 1.45;
    }

    .validation-icon {
        font-size: 16px;
        flex: 0 0 auto;
        margin-top: 1px;
    }

    .validation-message.error {
        background: #fdecea;
        color: #a03024;
        border: 1px solid #f2c4bf;
    }

    .validation-message.warning {
        background: #fdf6e7;
        color: #8a6116;
        border: 1px solid #f0dcae;
    }

    .email-preview {
        flex: 1;
        width: 100%;
        box-sizing: border-box;
        min-height: 320px;
        border: 1px solid #e1e8f0;
        border-radius: 6px;
        background: #ffffff;
    }

    .sms-preview {
        flex: 1;
        background: #f2f5f8;
        border: 1px solid #e1e8f0;
        border-radius: 6px;
        padding: 14px 18px 18px;
        min-height: 130px;
    }

    .sms-phone-header {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #9aa9b8;
        margin-bottom: 10px;
    }

    .sms-phone-icon {
        font-size: 14px;
    }

    .sms-bubble {
        background: #e5e5ea;
        color: #1c1c1e;
        border-radius: 18px;
        border-bottom-left-radius: 4px;
        padding: 10px 14px;
        max-width: 320px;
        font-size: 0.85rem;
        white-space: pre-wrap;
        word-break: break-word;
    }
</style>
